/**
 * useVoiceDictation — composes {@link useSpeechToText} with a RichTextEditor ref
 * to drive live voice dictation in the chat composer. Owns the AR/EN language
 * choice, the begin/confirm/cancel flow, and restoring the input on cancel, so
 * the host component only wires the returned state into its buttons.
 */

import { useRef, useState } from 'react';
import { useSpeechToText } from './useSpeechToText';
import type { RichTextEditorRef } from './RichTextEditor';

/** Voice language keys → BCP-47 tags the browser recognizer understands. */
const LANG_TO_BCP47: Record<VoiceLang, string> = {
  ar: 'ar-SA',
  en: 'en-US',
};

export type VoiceLang = 'ar' | 'en';

interface UseVoiceDictationOptions {
  /** The editor to dictate into. */
  editorRef: React.RefObject<RichTextEditorRef>;
  /**
   * Preferred language, e.g. from the app's i18n. Falls back to the browser
   * locale, then English. Only used to seed the initial choice.
   */
  speechLang?: string;
  /** Called after cancel restores the input, to re-sync host state (direction, etc.). */
  onRestore?: (markdown: string) => void;
}

export interface VoiceDictation {
  isSupported: boolean;
  isListening: boolean;
  lang: VoiceLang;
  setLang: (lang: VoiceLang) => void;
  /** Start dictation (remembers current input so cancel can restore it). */
  begin: () => void;
  /** Stop and keep the dictated text. */
  confirm: () => void;
  /** Stop and restore the input to what it was before dictation. */
  cancel: () => void;
}

const initialLang = (speechLang?: string): VoiceLang => {
  const raw =
    speechLang || (typeof navigator !== 'undefined' ? navigator.language : undefined);
  return raw?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
};

export function useVoiceDictation({
  editorRef,
  speechLang,
  onRestore,
}: UseVoiceDictationOptions): VoiceDictation {
  // Snapshot of the input before dictation started, for cancel-restore.
  const baseRef = useRef('');
  const [lang, setLang] = useState<VoiceLang>(() => initialLang(speechLang));

  const { isSupported, isListening, start, stop } = useSpeechToText({
    lang: LANG_TO_BCP47[lang],
    // Live dictation: commit finalized phrases, show the interim guess as a tail.
    onResult: (final, interim) => editorRef.current?.dictate(final, interim),
  });

  const begin = () => {
    editorRef.current?.endDictation();
    baseRef.current = editorRef.current?.getContent() ?? '';
    editorRef.current?.focus?.();
    start();
  };

  const confirm = () => {
    stop();
    editorRef.current?.endDictation();
    editorRef.current?.focus?.();
  };

  const cancel = () => {
    stop();
    editorRef.current?.endDictation();
    const base = baseRef.current;
    editorRef.current?.setContent(base);
    onRestore?.(base);
  };

  return { isSupported, isListening, lang, setLang, begin, confirm, cancel };
}

export default useVoiceDictation;
